import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Skeleton,
  useMediaQuery,
  Grid,
  Button,
} from "@mui/material";
import Swal from "sweetalert2";

import { NumberCellEditor } from "ag-grid-community";
import { fetchCustomerStatementJsonDynamic } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import { tokens } from "../../../../theme";
import { customerservice, reportsManagementUrl } from "../../../../config";
import { generateReport } from "../../../../data/DynamicTable/DynamicFunctions";

const GroupStatementRight = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomer, setFilteredCustomer] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

    const tourSteps = [
    {
      target: ".search-group-code",
      content: "Start by entering a group code here to load group data.",
    },
    {
      target: ".group-details-section",
      content: "This section shows detailed group and loan information.",
    },
    {
      target: ".date-range-inputs",
      content: "Select a date range for generating the statement.",
    },
    {
      target: ".generate-report-btn",
      content: "Click here to generate the group statement report.",
    },
  ];
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (searchTerm.length < 3) {
        setFilteredCustomer(null);
        return;
      }

      setIsFetching(true);
      try {
        const variables = { groupcode: searchTerm, companyid: null }; // Fix variable names

        const response = await fetchCustomerStatementJsonDynamic(
          "groupLoanAndSavings",
          null, // inputObjectName is not needed
          variables,
          customerservice.uri
        );

        const parsedResults = JSON.parse(response.results);
        if (parsedResults.first_name != null) {
          setFilteredCustomer(parsedResults);
        } else {
          setFilteredCustomer(null);
          Swal.fire(
            "Customer Not Found",
            "No matching customer found.",
            "warning"
          );
        }
      } catch (error) {
        console.error("Error fetching customer data:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCustomerData();
  }, [searchTerm]);

  const handleGenerateReport = async () => {
    const reportTypeId = 43;
    const reportType = "Group Statement";

    try {
      setIsLoading(true);
      const mutationData = {
        ReportTypeId: reportTypeId,
        ReportType: reportType,
        CustomerId: parseInt(filteredCustomer.id),
        StartDate: startDate,
        EndDate: endDate,
      };

      const newTab = window.open("", "_blank");
      newTab.document.write("<p>Loading...</p>");

      const response = await generateReport(
        mutationData,
        reportsManagementUrl.generate_report_uri,
        setIsLoading
      );

      if (response.ok) {
        const htmlContent = await response.text();
        newTab.document.open();
        newTab.document.write(htmlContent);
        newTab.document.close();
      } else {
        newTab.close();
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to generate the report. Please try again later.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while generating the report.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fields = filteredCustomer
    ? [
        {
          label: "LoanID",
          value: `${filteredCustomer.loanid || "N/A"}`,
        },
        { label: "First Name", value: filteredCustomer.first_name || "N/A" },
        {
          label: "Group Code",
          value: filteredCustomer.group_code || "N/A",
        },

        {
          label: "Date Created",
          value: filteredCustomer.date_created
            ? new Date(filteredCustomer.date_created).toLocaleDateString()
            : "N/A",
        },
        {
          label: "Loan Amount",
          value: `KSH ${filteredCustomer.active_loan || 0.0}`,
        },
        { label: "OLB", value: `KSH ${filteredCustomer.olb || 0.0}` },
        {
          label: "Arrears",
          value: `KSH ${filteredCustomer.loan_arrears || 0.0}`,
        },
        { label: "Days Overdue", value: filteredCustomer.days_overdue || 0 },
        {
          label: "BBF Account Balance",
          value: `KSH ${filteredCustomer.bbf_account_balance || 0.0}`,
        },
      ]
    : [];

  return (
    <Box sx={{ padding: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
  <Button
    variant="outlined"
    size="small"
    onClick={() => {
      setRunTour(true);
      setStepIndex(0);
    }}
  >
    Start Tour
  </Button>
</Box>

<Joyride
  steps={tourSteps}
  run={runTour}
  stepIndex={stepIndex}
  continuous
  showProgress
  showSkipButton
  styles={{
    options: {
      primaryColor: "#1976d2",
      zIndex: 9999,
    },
  }}
  callback={(data) => {
    const { status, index, type } = data;
    if (["finished", "skipped"].includes(status)) {
      setRunTour(false);
      setStepIndex(0);
    } else if (type === "step:after") {
      setStepIndex(index + 1);
    }
  }}
/>

      <Typography variant="h4">Group Statement</Typography>
      <TextField
        className="search-group-code"
        label="Search Group by Code"
        placeholder="Enter Group Code"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          endAdornment: isFetching && (
            <CircularProgress size={24} sx={{ marginRight: 2 }} />
          ),
        }}
      />

      {filteredCustomer && (
        <>
          <Box
          className="group-details-section"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              padding: { xs: 1, sm: 2 },
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  textAlign: { xs: "center", sm: "left" },
                  mb: 3,
                  fontSize: "1rem",
                }}
              >
                Group Details
              </Typography>
              {isSmallScreen ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    background: colors.primary[200],
                    borderRadius: 2,
                    padding: 2,
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  {fields.map((field, index) => (
                    <Grid
                      container
                      key={index}
                      sx={{
                        borderBottom:
                          index !== fields.length - 1
                            ? "1px solid #ccc"
                            : "none",
                        py: 1,
                      }}
                    >
                      <Grid item xs={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {field.label}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ textAlign: "right" }}>
                          {field.value}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    background: `${colors.primary[200]} !important`,
                    borderRadius: 2,
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                    overflowX: "auto",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        {fields.map((field, index) => (
                          <TableCell
                            key={index}
                            sx={{
                              background: colors.primary[400],
                              fontSize: "0.65rem",
                              fontWeight: "bold",
                            }}
                          >
                            {field.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {fields.map((field, index) => (
                          <TableCell key={index} sx={{ fontSize: "0.73rem" }}>
                            {field.value}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  textAlign: { xs: "center", sm: "left" },
                  mb: 2,
                  fontSize: "1rem",
                }}
              >
                Generate Group Statement
              </Typography>
              <Box
              className="date-range-inputs" 
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  background: colors.primary[200],
                  borderRadius: 2,
                  padding: { xs: 1.5, sm: 2 },
                  maxWidth: "300px",
                }}
              >
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={{ background: colors.primary[400], borderRadius: 1 }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  sx={{ background: colors.primary[400], borderRadius: 1 }}
                />
                <Button
                  className="generate-report-btn"
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateReport}
                  disabled={isLoading}
                  fullWidth
                  size="small"
                  sx={{
                    background: colors.blueAccent[500],
                    "&:hover": { background: colors.blueAccent[600] },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Generate Report"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default GroupStatementRight;
